import React, { useState, useEffect, useRef } from 'react';
import type { DrillDrawingPoint } from '../types';
import { Undo2 } from 'lucide-react';

interface DrillDrawingCanvasProps {
  width?: number;
  height?: number;
  points?: DrillDrawingPoint[];
  onChange?: (points: DrillDrawingPoint[]) => void;
  readOnly?: boolean;
}

export default function DrillDrawingCanvas({
  width = 400,
  height = 300,
  points: initialPoints = [],
  onChange,
  readOnly = false
}: DrillDrawingCanvasProps) {
  const [isDrawing, setIsDrawing] = useState(false);
  const [diagramSize, setDiagramSize] = useState({ width, height });
  const svgRef = useRef<SVGSVGElement>(null);
  const [currentPath, setCurrentPath] = useState<DrillDrawingPoint[]>([]);
  const [paths, setPaths] = useState<DrillDrawingPoint[][]>([]);

  // Adjust diagram size for mobile
  useEffect(() => {
    const updateSize = () => {
      if (window.innerWidth < 768) {
        const containerWidth = Math.min(window.innerWidth - 64, width);
        const aspectRatio = height / width;
        const containerHeight = containerWidth * aspectRatio;
        setDiagramSize({ 
          width: containerWidth, 
          height: containerHeight 
        });
      } else {
        setDiagramSize({ width, height });
      }
    };

    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, [width, height]);

  // Initialize paths from props
  useEffect(() => {
    if (initialPoints && initialPoints.length > 0) {
      // Convert flat points array to paths array
      const newPaths: DrillDrawingPoint[][] = [];
      let currentPathPoints: DrillDrawingPoint[] = [];
      
      initialPoints.forEach((point, index) => {
        if (point.x === -1 && point.y === -1) {
          // Special point indicating path break
          if (currentPathPoints.length > 0) {
            newPaths.push(currentPathPoints);
            currentPathPoints = [];
          }
        } else {
          currentPathPoints.push(point);
          if (index === initialPoints.length - 1 && currentPathPoints.length > 0) {
            newPaths.push(currentPathPoints);
          }
        }
      });
      
      setPaths(newPaths);
    }
  }, [initialPoints]);

  const toSvgPoint = (clientX: number, clientY: number) => {
    if (!svgRef.current) return null;
    const rect = svgRef.current.getBoundingClientRect();
    const viewBox = { width: 100, height: 100, minX: 0, minY: 0 };
    const relativeX = (clientX - rect.left) / rect.width;
    const relativeY = (clientY - rect.top) / rect.height;
    return {
      x: viewBox.minX + (relativeX * viewBox.width),
      y: viewBox.minY + (relativeY * viewBox.height)
    };
  };

  const startDrawing = (x: number, y: number) => {
    if (readOnly) return;
    setIsDrawing(true);
    setCurrentPath([{ x, y }]);
  };

  const draw = (x: number, y: number) => {
    if (!isDrawing || readOnly) return;
    setCurrentPath(prev => [...prev, { x, y }]);
  };

  const endDrawing = () => {
    if (!isDrawing || readOnly) return;
    setIsDrawing(false);
    if (currentPath.length > 0) {
      const newPaths = [...paths, currentPath];
      setPaths(newPaths);
      setCurrentPath([]);
      
      // Convert paths to flat array with separators for onChange
      const flatPoints: DrillDrawingPoint[] = [];
      newPaths.forEach((path, index) => {
        flatPoints.push(...path);
        if (index < newPaths.length - 1) {
          // Add separator point between paths
          flatPoints.push({ x: -1, y: -1 });
        }
      });
      onChange?.(flatPoints);
    }
  };

  const handleUndo = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (paths.length > 0) {
      const newPaths = paths.slice(0, -1);
      setPaths(newPaths);
      
      // Convert remaining paths to flat array
      const flatPoints: DrillDrawingPoint[] = [];
      newPaths.forEach((path, index) => {
        flatPoints.push(...path);
        if (index < newPaths.length - 1) {
          flatPoints.push({ x: -1, y: -1 });
        }
      });
      onChange?.(flatPoints);
    }
  };

  const handleMouseDown = (e: React.MouseEvent<SVGSVGElement>) => {
    const point = toSvgPoint(e.clientX, e.clientY);
    if (point) {
      startDrawing(point.x, point.y);
    }
  };

  const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const point = toSvgPoint(e.clientX, e.clientY);
    if (point) {
      draw(point.x, point.y);
    }
  };

  const handleTouchStart = (e: React.TouchEvent<SVGSVGElement>) => {
    e.preventDefault();
    const touch = e.touches[0];
    const point = toSvgPoint(touch.clientX, touch.clientY);
    if (point) {
      startDrawing(point.x, point.y);
    }
  };

  const handleTouchMove = (e: React.TouchEvent<SVGSVGElement>) => {
    e.preventDefault();
    const touch = e.touches[0];
    const point = toSvgPoint(touch.clientX, touch.clientY);
    if (point) {
      draw(point.x, point.y);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button
          onClick={handleUndo}
          disabled={paths.length === 0 || readOnly}
          className="px-3 py-1.5 text-sm text-[#1b3968] hover:text-[#1b3968]/80 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
        >
          <Undo2 className="h-4 w-4" />
          Undo
        </button>
      </div>
      <div className="flex justify-center items-center w-full">
        <svg
          ref={svgRef}
          width={diagramSize.width}
          height={diagramSize.height}
          viewBox="0 0 100 100"
          className={`border-4 border-[#1b3968] rounded-lg ${!readOnly ? 'cursor-crosshair' : ''}`}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={endDrawing}
          onMouseLeave={endDrawing}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={endDrawing}
        >
          {/* Background Image */}
          <image
            href="https://cdn.shopify.com/s/files/1/0609/6397/9463/files/Half_hockey_rink.png?v=1743095801"
            x="0"
            y="0"
            width="100"
            height="100"
            preserveAspectRatio="none"
          />

          {/* Grid lines */}
          <g stroke="#d1d5db" strokeWidth="0.5">
            {[25, 50, 75].map((pos) => (
              <React.Fragment key={pos}>
                <line x1={pos} y1="0" x2={pos} y2="100" />
                <line x1="0" y1={pos} x2="100" y2={pos} />
              </React.Fragment>
            ))}
          </g>

          {/* Completed paths */}
          {paths.map((path, pathIndex) => (
            <path
              key={pathIndex}
              d={`M ${path.map(p => `${p.x},${p.y}`).join(' L ')}`}
              stroke="#ef4444"
              strokeWidth="0.75"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          ))}

          {/* Current drawing path */}
          {currentPath.length > 0 && (
            <path
              d={`M ${currentPath.map(p => `${p.x},${p.y}`).join(' L ')}`}
              stroke="#ef4444"
              strokeWidth="0.75"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          )}
        </svg>
      </div>
    </div>
  );
}